import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const THRESHOLD = 72;
const MAX_PULL = 120;
const HOLD = 56;

export const PullToRefresh = ({ onRefresh, children, accent = '#EAB308', disabled = false }) => {
  const ref = useRef(null);
  const startY = useRef(null);
  const pullRef = useRef(0);
  const armed = useRef(false);
  const busy = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || disabled) return;

    const update = (v) => { pullRef.current = v; setPull(v); };

    const onStart = (e) => {
      if (busy.current || window.scrollY > 0) { startY.current = null; return; }
      startY.current = e.touches[0].clientY;
      armed.current = false;
    };

    const onMove = (e) => {
      if (startY.current === null || busy.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0 && pullRef.current === 0) { startY.current = null; return; }
      if (window.scrollY > 0) return;
      if (e.cancelable) e.preventDefault();
      setDragging(true);
      const next = Math.min(Math.max(dy, 0) * 0.55, MAX_PULL);
      if (!armed.current && next >= THRESHOLD) { armed.current = true; navigator.vibrate?.(10); }
      if (armed.current && next < THRESHOLD) armed.current = false;
      update(next);
    };

    const onEnd = async () => {
      if (startY.current === null) return;
      startY.current = null;
      setDragging(false);
      if (pullRef.current >= THRESHOLD && !busy.current) {
        busy.current = true;
        setRefreshing(true);
        update(HOLD);
        try { await onRefresh(); } finally {
          setRefreshing(false);
          update(0);
          busy.current = false;
        }
      } else {
        update(0);
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [onRefresh, disabled]);

  const progress = Math.min(pull / THRESHOLD, 1);
  const ease = dragging ? 'none' : 'transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s';

  return (
    <div ref={ref} className="relative" data-testid="pull-to-refresh" data-refreshing={refreshing ? 'true' : 'false'}>
      <div
        className="pointer-events-none absolute inset-x-0 flex justify-center"
        style={{ top: -52, transform: `translateY(${pull}px)`, opacity: progress, transition: ease }}
        aria-hidden
      >
        <div
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.08] bg-[#18181b] shadow-lg"
          data-testid="pull-indicator"
        >
          <RefreshCw
            className={`h-[18px] w-[18px] ${refreshing ? 'animate-spin' : ''}`}
            style={{ color: accent, transform: refreshing ? undefined : `rotate(${pull * 2.5}deg)`, opacity: 0.4 + progress * 0.6 }}
          />
        </div>
      </div>
      <div style={{ transform: `translateY(${pull}px)`, transition: ease }}>{children}</div>
    </div>
  );
};
